import { useState, useEffect } from 'react';
import { ArrowLeft, Shield, Swords, Printer } from 'lucide-react';

interface Sheet {
  id: string;
  name: string;
  description: string;
  imageUrl?: string;
  bonuses: Array<{ attribute: string; value: number }>;
  maluses: Array<{ attribute: string; value: number }>;
  moves: Array<{ name: string; description: string }>;
  alignment?: string;
  notes?: string;
  damage?: number;
  armor?: number;
  maxHP?: number;
  level?: number;
}

const TEAM_PRINT_COLORS: Record<string, string> = {
  BLUE: '#3b82f6',
  RED: '#ef4444',
  GREEN: '#22c55e',
  YELLOW: '#eab308',
};

interface HeroSheetsTabProps {
  selectedSheetId?: string | null;
  teamColor?: string | null;
  onSheetViewed?: () => void;
}

function HeroSheetsTab({ selectedSheetId, teamColor, onSheetViewed }: HeroSheetsTabProps) {
  const [sheets, setSheets] = useState<Sheet[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSheet, setSelectedSheet] = useState<Sheet | null>(null);

  useEffect(() => {
    fetchSheets();
  }, []);

  // When selectedSheetId changes from outside, fetch and display that sheet
  useEffect(() => {
    if (selectedSheetId && sheets.length > 0) {
      const found = sheets.find((s) => s.id === selectedSheetId);
      if (found) {
        setSelectedSheet(found);
      } else {
        // Sheet not in cached list, fetch it directly
        fetch(`/api/sheets/${selectedSheetId}`)
          .then((res) => res.json())
          .then((data) => { if (data.sheet) setSelectedSheet(data.sheet); })
          .catch(() => {});
      }
    }
  }, [selectedSheetId, sheets]);

  const handleBack = () => {
    setSelectedSheet(null);
    onSheetViewed?.();
  };

  const fetchSheets = async () => {
    try {
      const response = await fetch('/api/sheets');
      if (response.ok) {
        const data = await response.json();
        setSheets(data.sheets || []);
      }
    } catch (error) {
      console.error('[Master] Error fetching sheets:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-purple-500 mx-auto mb-4"></div>
          <p className="text-xl text-gray-300">Caricamento schede...</p>
        </div>
      </div>
    );
  }

  // Detail view for a selected sheet
  if (selectedSheet) {
    const printBorderColor = teamColor ? TEAM_PRINT_COLORS[teamColor] || '#6b7280' : '#6b7280';

    return (
      <div className="h-full flex flex-col overflow-hidden">
        <style>{`
          @media print {
            body * { visibility: hidden !important; }
            #sheet-print-area, #sheet-print-area * { visibility: visible !important; }
            #sheet-print-area {
              position: absolute !important;
              left: 0 !important;
              top: 0 !important;
              width: 100% !important;
              padding: 20px !important;
              background: white !important;
              color: black !important;
              border: 4px solid ${printBorderColor} !important;
              border-radius: 12px !important;
            }
            #sheet-print-area * {
              background: transparent !important;
              background-image: none !important;
              color: black !important;
              -webkit-text-fill-color: black !important;
            }
            #sheet-print-area img {
              -webkit-print-color-adjust: exact !important;
              print-color-adjust: exact !important;
            }
            #sheet-print-area .print-border-green { border-color: #22c55e !important; }
            #sheet-print-area .print-border-red { border-color: #ef4444 !important; }
            #sheet-print-area .print-border-yellow { border-color: #eab308 !important; }
            #sheet-print-area .print-border-purple { border-color: #a855f7 !important; }
            #sheet-print-area .print-border-gray { border-color: #9ca3af !important; }
            #sheet-print-area .print-section-border { border: 1px solid #d1d5db !important; }
            #sheet-print-area h3.print-title-green { color: #16a34a !important; -webkit-text-fill-color: #16a34a !important; }
            #sheet-print-area h3.print-title-red { color: #dc2626 !important; -webkit-text-fill-color: #dc2626 !important; }
            #sheet-print-area h3.print-title-yellow { color: #ca8a04 !important; -webkit-text-fill-color: #ca8a04 !important; }
            #sheet-print-area h3.print-title-purple { color: #9333ea !important; -webkit-text-fill-color: #9333ea !important; }
            #sheet-print-area .print-value-green { color: #16a34a !important; -webkit-text-fill-color: #16a34a !important; }
            #sheet-print-area .print-value-red { color: #dc2626 !important; -webkit-text-fill-color: #dc2626 !important; }
          }
        `}</style>

        <div className="flex-shrink-0 p-4 pb-0 flex items-center justify-between">
          <button
            onClick={handleBack}
            className="flex items-center gap-2 text-gray-400 hover:text-white transition"
          >
            <ArrowLeft size={20} />
            Torna all'elenco
          </button>
          <button
            onClick={() => window.print()}
            className="flex items-center gap-2 text-gray-400 hover:text-white bg-gray-800 hover:bg-gray-700 px-4 py-2 rounded-lg transition"
          >
            <Printer size={16} />
            Stampa scheda
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div id="sheet-print-area" className="max-w-3xl mx-auto">
            {/* Header */}
            <div className="bg-gradient-to-r from-purple-900 to-indigo-800 rounded-xl p-5 border-2 border-purple-500/50 mb-4">
              <div className="flex items-start gap-4">
                {selectedSheet.imageUrl && (
                  <img
                    src={selectedSheet.imageUrl}
                    alt={selectedSheet.name}
                    className="w-24 h-24 rounded-full object-cover border-4 border-white/30"
                  />
                )}
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-white mb-2">{selectedSheet.name}</h1>
                  <p className="text-white/80">{selectedSheet.description}</p>
                </div>
              </div>
            </div>

            {/* Combat Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
              <div className="bg-gray-800 rounded-lg p-3 border border-gray-600 text-center">
                <p className="text-xs text-gray-400 uppercase mb-1">Danno</p>
                <p className="text-2xl font-bold text-white">{selectedSheet.damage ?? 0}</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-3 border border-gray-600 text-center">
                <p className="text-xs text-gray-400 uppercase mb-1">Armatura</p>
                <p className="text-2xl font-bold text-white">{selectedSheet.armor ?? 0}</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-3 border border-gray-600 text-center">
                <p className="text-xs text-gray-400 uppercase mb-1">PF Max</p>
                <p className="text-2xl font-bold text-white">{selectedSheet.maxHP ?? 10}</p>
              </div>
              <div className="bg-gray-800 rounded-lg p-3 border border-gray-600 text-center">
                <p className="text-xs text-gray-400 uppercase mb-1">Livello</p>
                <p className="text-2xl font-bold text-white">{selectedSheet.level ?? 1}</p>
              </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
              {selectedSheet.bonuses && selectedSheet.bonuses.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-4 border border-green-500/30 print-section-border print-border-green">
                  <h3 className="text-lg font-semibold text-green-400 mb-3 flex items-center gap-2 print-title-green">
                    <Shield size={18} />
                    Bonus
                  </h3>
                  <ul className="space-y-2">
                    {selectedSheet.bonuses.map((bonus, idx) => (
                      <li key={idx} className="text-gray-300 flex justify-between">
                        <span>{bonus.attribute}</span>
                        <span className="text-green-400 font-bold print-value-green">+{bonus.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {selectedSheet.maluses && selectedSheet.maluses.length > 0 && (
                <div className="bg-gray-800 rounded-lg p-4 border border-red-500/30 print-section-border print-border-red">
                  <h3 className="text-lg font-semibold text-red-400 mb-3 flex items-center gap-2 print-title-red">
                    <Swords size={18} />
                    Malus
                  </h3>
                  <ul className="space-y-2">
                    {selectedSheet.maluses.map((malus, idx) => (
                      <li key={idx} className="text-gray-300 flex justify-between">
                        <span>{malus.attribute}</span>
                        <span className="text-red-400 font-bold print-value-red">{malus.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            {/* Alignment */}
            {selectedSheet.alignment && (
              <div className="bg-gray-800 rounded-lg p-4 border border-yellow-500/30 mb-4 print-section-border print-border-yellow">
                <h3 className="text-lg font-semibold text-yellow-400 mb-2 print-title-yellow">Allineamento</h3>
                <p className="text-white">{selectedSheet.alignment}</p>
              </div>
            )}

            {/* Moves */}
            {selectedSheet.moves && selectedSheet.moves.length > 0 && (
              <div className="bg-gray-800 rounded-lg p-4 border border-purple-500/30 mb-4 print-section-border print-border-purple">
                <h3 className="text-lg font-semibold text-purple-400 mb-3 print-title-purple">Mosse</h3>
                <div className="space-y-3">
                  {selectedSheet.moves.map((move, idx) => (
                    <div key={idx} className="bg-gray-900 rounded-lg p-3 print-section-border">
                      <h4 className="font-semibold text-white mb-1">{move.name}</h4>
                      <p className="text-gray-400 text-sm">{move.description}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Notes */}
            {selectedSheet.notes && (
              <div className="bg-gray-800 rounded-lg p-4 border border-gray-600 mb-4 print-section-border print-border-gray">
                <h3 className="text-lg font-semibold text-gray-400 mb-2">Note</h3>
                <p className="text-gray-300 whitespace-pre-wrap">{selectedSheet.notes}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // List view
  if (sheets.length === 0) {
    return (
      <div className="h-full flex items-center justify-center">
        <div className="text-center">
          <p className="text-2xl text-gray-400">Nessuna scheda eroe disponibile</p>
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col overflow-hidden">
      <div className="flex-shrink-0 p-6 pb-2">
        <h1 className="text-3xl font-bold text-white">Schede Eroi</h1>
        <p className="text-gray-400 mt-1">{sheets.length} schede disponibili</p>
      </div>

      <div className="flex-1 overflow-y-auto p-6 pt-2">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {sheets.map((sheet) => (
            <button
              key={sheet.id}
              onClick={() => setSelectedSheet(sheet)}
              className="bg-gray-800 hover:bg-gray-700 border border-gray-700 hover:border-purple-500/50 rounded-xl p-4 text-left transition group"
            >
              <div className="flex items-center gap-3 mb-3">
                {sheet.imageUrl ? (
                  <img
                    src={sheet.imageUrl}
                    alt={sheet.name}
                    className="w-14 h-14 rounded-full object-cover border-2 border-purple-500/30 group-hover:border-purple-500/60 transition"
                  />
                ) : (
                  <div className="w-14 h-14 rounded-full bg-purple-900/50 flex items-center justify-center border-2 border-purple-500/30 text-2xl">
                    ⚔️
                  </div>
                )}
                <h3 className="text-lg font-bold text-white group-hover:text-purple-300 transition">
                  {sheet.name}
                </h3>
              </div>
              <p className="text-gray-400 text-sm line-clamp-2">{sheet.description}</p>
              <div className="flex gap-2 mt-3 flex-wrap">
                {sheet.bonuses?.length > 0 && (
                  <span className="text-xs bg-green-900/40 text-green-400 px-2 py-0.5 rounded">
                    {sheet.bonuses.length} bonus
                  </span>
                )}
                {sheet.maluses?.length > 0 && (
                  <span className="text-xs bg-red-900/40 text-red-400 px-2 py-0.5 rounded">
                    {sheet.maluses.length} malus
                  </span>
                )}
                {sheet.moves?.length > 0 && (
                  <span className="text-xs bg-purple-900/40 text-purple-400 px-2 py-0.5 rounded">
                    {sheet.moves.length} mosse
                  </span>
                )}
              </div>
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default HeroSheetsTab;
